<template>
  <div class="chat-page">
    <van-nav-bar :title="navTitle" fixed left-text="返回" left-arrow @click-left="goBack" />

    <!-- 未登录 -->
    <div v-if="!isLogin" class="not-login">
      <van-icon name="service-o" size="60" color="#ff6b35" />
      <p>请先登录后再联系客服</p>
      <van-button type="primary" round @click="$router.push('/login')">去登录</van-button>
    </div>

    <template v-else>
      <!-- 连接中 -->
      <div v-if="!session" class="loading">
        <van-loading color="#ff6b35" />
        <p>正在连接客服...</p>
      </div>

      <!-- 聊天窗口 -->
      <div v-else class="chat-body">
        <div class="message-list" ref="messageList">
          <div v-if="!messages.length" class="empty-tip">
            {{ shopName ? ('您好，这里是「' + shopName + '」客服，请问有什么可以帮您？') : '您好，我是商城官方客服，请问有什么可以帮您？' }}
          </div>
          <div
            v-for="msg in messages"
            :key="msg.id"
            class="message-item"
            :class="[msg.sender_type === 1 ? 'mine' : 'agent', { recalled: msg.revoked === 1 }]"
          >
            <!-- 撤回的消息：居中显示系统提示 -->
            <div v-if="msg.revoked === 1" class="recalled-tip">
              {{ msg.sender_type === 1 ? '你撤回了一条消息' : '对方撤回了一条消息' }}
            </div>

            <template v-else>
              <img
                class="avatar"
                :class="{ 'avatar-clickable': msg.sender_type !== 1 }"
                :src="msg.sender_type === 1 ? userAvatar : agentAvatar"
                alt="头像"
                :title="msg.sender_type !== 1 ? '点击查看店铺' : ''"
                @click="onAgentAvatarClick(msg)"
                @error="onAvatarError"
              />
              <div class="msg-col">
                <!-- 图片消息：默认显示缩略图，点击查看原图（长按 / 右键可撤回） -->
                <div
                  v-if="msg.msg_type === 2"
                  class="bubble bubble-img"
                  @click="onImageClick(msg)"
                  @touchstart="onMsgTouchStart(msg)"
                  @touchend="onMsgTouchEnd(msg, $event)"
                  @touchmove="cancelMsgLongPress"
                  @touchcancel="cancelMsgLongPress"
                  @mousedown="onMsgMouseDown(msg)"
                  @mouseup="onMsgMouseUp(msg, $event)"
                  @mouseleave="cancelMsgLongPress"
                  @contextmenu.prevent="openMsgMenu(msg)"
                >
                  <img :src="thumbUrl(msg.content)" alt="图片" loading="lazy" @error="onThumbError(msg.content)" />
                </div>
                <!-- 文本消息（自动识别网址：点击即可打开） -->
                <div
                  v-else
                  class="bubble"
                  @touchstart="onMsgTouchStart(msg)"
                  @touchend="onMsgTouchEnd(msg, $event)"
                  @touchmove="cancelMsgLongPress"
                  @touchcancel="cancelMsgLongPress"
                  @mousedown="onMsgMouseDown(msg)"
                  @mousemove="cancelMsgLongPress"
                  @mouseup="onMsgMouseUp(msg, $event)"
                  @mouseleave="cancelMsgLongPress"
                  @contextmenu="onMsgContextMenu(msg, $event)"
                >
                  <template v-for="(seg, i) in msgSegments(msg.content)">
                    <a
                      v-if="seg.type === 'link'"
                      :key="'link' + i"
                      class="msg-link"
                      :href="seg.href"
                      @click.stop.prevent="onLinkClick(seg.href)"
                    >{{ seg.text }}</a>
                    <span v-else :key="'text' + i">{{ seg.text }}</span>
                  </template>
                </div>
                <!-- 已读回执：我发出的消息显示「客服是否已读」（长按气泡可撤回 2 分钟内的消息） -->
                <span
                  v-if="msg.sender_type === 1"
                  class="read-state"
                  :class="{ read: isAgentRead(msg) }"
                >{{ isAgentRead(msg) ? '已读' : '未读' }}</span>
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- 输入区 -->
      <div v-if="session" class="chat-input">
        <input
          ref="fileInput"
          type="file"
          accept="image/*"
          style="display: none;"
          @change="onFileChange"
        />
        <van-icon name="photograph" class="img-btn" @click="$refs.fileInput.click()" />
        <input
          class="inp"
          type="text"
          v-model.trim="inputText"
          maxlength="500"
          placeholder="请输入您要咨询的内容..."
          @keyup.enter="send"
        >
        <button class="send-btn" @click="send">发送</button>
      </div>
    </template>

    <!-- 图片查看器：支持双击 / 双指捏合 / 滚轮缩放，拖动查看细节，长图默认按宽度显示 -->
    <ImageViewer
      v-model="previewVisible"
      :url="previewUrl"
      :thumb="previewThumb"
    />

    <!-- 长按消息操作菜单：复制 / 选择文字 / 复制链接 / 查看大图 / 撤回（2 分钟内可撤回自己的消息） -->
    <van-action-sheet
      v-model="showMsgMenu"
      :actions="msgMenuActions"
      cancel-text="取消"
      close-on-click-action
      @select="onMsgMenuSelect"
    />

    <!-- 选择文字：可长按拖动选中消息中的任意片段后复制（像微信一样自由复制聊天内容） -->
    <van-popup
      v-model="showTextSelect"
      position="bottom"
      round
      class="text-select-popup"
    >
      <div class="ts-header">
        <span class="ts-title">选择文字</span>
        <van-icon name="cross" class="ts-close" @click="showTextSelect = false" />
      </div>
      <p class="ts-tip">长按下方文字并拖动选中需要的片段，再点「复制选中」；也可以直接点「复制全部」。</p>
      <div ref="selectTextBox" class="ts-text">{{ selectText }}</div>
      <div class="ts-actions">
        <van-button block round type="primary" @click="copySelection">复制选中</van-button>
        <van-button block round plain type="primary" @click="copyMsgText(selectText)">复制全部</van-button>
      </div>
    </van-popup>

    <!-- 一条消息里含多个网址时：先选择要复制的那一个 -->
    <van-action-sheet
      v-model="showLinkMenu"
      :actions="linkActions"
      cancel-text="取消"
      close-on-click-action
      @select="onLinkMenuSelect"
    />
  </div>
</template>

<script>
import { createChatSessionApi, sendChatMessageApi, getChatMessagesApi, uploadChatImageApi, revokeChatMessageApi } from '@/api/chat'
import { getChatMessageCache, setChatMessageCache, setChatNotifiedMsgId } from '@/utils/chat'
import { splitTextLinks, findLinks, isInternalUrl, toInternalPath, isHttpUrl } from '@/utils/link'
import { copyText } from '@/utils/clipboard'
import chatSocket from '@/utils/ws'
import { Toast, Dialog } from 'vant'
import ImageViewer from '@/components/ImageViewer.vue'

// 客服默认头像（客服真实头像统一存放在服务器 /uploads/service_avatar/ 下）
const SERVICE_AVATAR = '/uploads/service_avatar/customer_service_avatar.png'
// 本地兜底头像（客服头像加载失败 / 用户未设置头像时使用）
const DEFAULT_AVATAR = require('@/assets/default-avatar.svg')

export default {
  name: 'Chat',
  components: { ImageViewer },
  data () {
    return {
      session: null,
      messages: [],
      lastId: 0,
      inputText: '',
      pollTimer: null,
      sending: false,
      // 图片查看器（自定义放大查看）
      previewVisible: false,
      previewUrl: '',
      previewThumb: '',
      // 客服已读游标：消息 id <= 该值表示客服已读（用于“客服已读我”回执）
      agentReadId: 0,
      // 缩略图加载失败的原图 URL 集合（用于回退显示原图）
      failedThumbs: {},
      // WebSocket 是否已连接（连接成功后不再依赖 HTTP 轮询）
      wsReady: false,
      // 等待 WebSocket 回执的消息：clientMsgId -> resolve
      pendingAcks: {},
      agentAvatar: SERVICE_AVATAR,
      userAvatar: DEFAULT_AVATAR,
      serviceAvatar: SERVICE_AVATAR,
      defaultAvatar: DEFAULT_AVATAR,
      // C2C：当前会话对应的商家（0=商城官方客服）
      merchantId: 0,
      // 店铺主页 id（官方客服渠道也会返回官方自营店铺 id，用于「点击客服头像看店铺」）
      shopId: 0,
      shopName: '',
      shopLogo: '',
      // 入口传入的店铺名称（如从「聊天」列表点进某个商家）
      routeShopName: '',
      // 长按消息操作菜单（撤回 / 复制）
      showMsgMenu: false,
      msgMenuActions: [],
      activeMsg: null,
      longPressTimer: null,
      // 长按已触发（用于屏蔽随后的 click，例如图片预览）
      longPressTriggered: false,
      // 撤回请求进行中
      revoking: false,
      // 「选择文字」弹层（消息文字自由复制）
      showTextSelect: false,
      selectText: '',
      // 「复制链接」：一条消息含多个网址时的选择菜单
      showLinkMenu: false,
      linkActions: []
    }
  },
  computed: {
    isLogin () {
      return !!this.$store.getters.token
    },
    // 顶部标题：显示对应店铺客服名称（官方客服 / 商家客服）
    navTitle () {
      if (this.shopName) {
        return this.shopName 
      }
      return Number(this.merchantId) > 0 ? '商家客服' : '联系客服'
    }
  },
  watch: {
    // 登录失效后立即停止轮询并复位，避免持续请求失败接口
    isLogin (val) {
      if (!val) {
        this.stopPolling()
        this.unbindSocket()
        chatSocket.disconnect()
        this.session = null
        this.messages = []
      }
    },
    // C2C：在聊天页内切换不同商家客服（query 变化，组件被复用不会重新 created）时重新建会话
    '$route.query.merchantId' (val) {
      const next = Number(val) || 0
      this.routeShopName = this.$route.query.shopName || ''
      if (next === Number(this.merchantId) && this.session) {
        return
      }
      this.merchantId = next
      if (this.isLogin) {
        this.stopPolling()
        this.unbindSocket()
        this.session = null
        this.messages = []
        this.lastId = 0
        this.agentReadId = 0
        setChatMessageCache(0, [], 0)
        this.initChat()
      }
    }
  },
  created () {
    // C2C：支持 /chat?merchantId=2&shopName=xxx 直接进入指定商家客服
    this.merchantId = Number(this.$route.query.merchantId) || 0
    this.routeShopName = this.$route.query.shopName || ''
    // 消息文本 → 链接片段 的解析缓存（非响应式，避免每次重渲染重复正则解析）
    this._segCache = {}
    if (this.isLogin) {
      this.initChat()
    }
  },
  beforeDestroy () {
    this.stopPolling()
    this.unbindSocket()
    this.cancelMsgLongPress()
  },
  methods: {
    // 返回（无历史记录时回到个人中心）
    goBack () {
      if (window.history.length > 1) {
        this.$router.back()
      } else {
        this.$router.replace('/user')
      }
    },
    // 已读回执：该消息是否已被客服阅读（消息 id <= 客服已读游标）
    isAgentRead (msg) {
      return Number(msg && msg.id) > 0 && Number(msg.id) <= Number(this.agentReadId)
    },
    // 点击客服头像 → 进入店铺主页（商家资质需安全验证后查看）
    onAgentAvatarClick (msg) {
      if (!msg || Number(msg.sender_type) === 1) return
      if (!this.shopId) {
        Toast('该客服暂未关联店铺')
        return
      }
      if (this.$route.path !== '/shop/' + this.shopId) {
        this.$router.push('/shop/' + this.shopId)
      }
    },
    async initChat () {
      try {
        // C2C：按 URL 中的 merchantId 打开对应商家客服会话（0=官方客服）
        const { data } = await createChatSessionApi(this.merchantId)
        const session = data.session || null
        const prevSessionId = this.session ? Number(this.session.id) : 0
        const nextSessionId = session ? Number(session.id) : 0
        // 切换到另一个客服会话时清空旧会话消息，避免不同店铺的消息混在一起
        // （并清空内存缓存，保证历史记录重新从服务端完整拉取）
        if (this.messages.length && prevSessionId !== nextSessionId) {
          this.messages = []
          this.lastId = 0
          setChatMessageCache(0, [], 0)
        }
        this.session = session
        // 标题优先使用入口传入的店铺名（官方自营店铺沿用官方客服渠道时也能显示店铺名）
        if (data.shop && data.shop.shop_name) {
          this.shopName = data.shop.shop_name
          this.shopLogo = data.shop.shop_logo || ''
        } else if (this.routeShopName) {
          this.shopName = this.routeShopName
        }
        // 店铺主页 id：点击客服头像可进入对应店铺页面
        if (data.shop && data.shop.shop_id) {
          this.shopId = Number(data.shop.shop_id) || 0
        }
        // 服务端返回客服已读游标，用于“客服已读我”回执
        this.agentReadId = Number(data.session && data.session.agent_last_read_id) || 0
        if (data.user && data.user.avatar) {
          this.userAvatar = data.user.avatar
        }
        // 复用内存缓存：避免每次进入聊天页都重新加载全部历史消息
        const cache = getChatMessageCache()
        if (cache.sessionId === this.session.id && cache.messages.length) {
          this.messages = cache.messages.slice()
          this.lastId = cache.lastId
        } else {
          this.messages = []
          this.lastId = 0
        }
        // 拉取消息（lastId=0 时服务端返回完整历史记录）
        await this.loadMessages()
        // 建立 WebSocket 实时通道（不可用时自动降级为 HTTP 轮询）
        this.bindSocket()
        chatSocket.connect('user')
        this.startPolling()
      } catch (err) {
        Toast(typeof err === 'string' ? err : '连接客服失败')
      }
    },

    // ==================== WebSocket 实时通道 ====================
    bindSocket () {
      this.unbindSocket()
      this._onWsOpen = () => {
        this.wsReady = true
        // 重连成功后补拉一次，避免断线期间漏掉的消息
        this.loadMessages().catch(() => {})
      }
      this._onWsMessage = (payload) => {
        if (!this.session || Number(payload.sessionId) !== Number(this.session.id)) return
        this.appendMessages([payload.message])
        if (payload.session && payload.session.agent_last_read_id) {
          const cursor = Number(payload.session.agent_last_read_id) || 0
          if (cursor > this.agentReadId) this.agentReadId = cursor
        }
        // 收到客服消息即上报已读，客服端可实时看到「买家已读」
        chatSocket.sendRead(this.session.id, this.lastId)
      }
      // 自己发出的消息：服务端入库成功后回执真实消息 id
      this._onWsAck = (payload) => {
        if (payload.clientMsgId && this.pendingAcks[payload.clientMsgId]) {
          this.pendingAcks[payload.clientMsgId](payload.message)
          delete this.pendingAcks[payload.clientMsgId]
        }
      }
      this._onWsRead = (payload) => {
        if (!this.session || Number(payload.sessionId) !== Number(this.session.id)) return
        const cursor = Number(payload.agent_last_read_id) || 0
        if (cursor > this.agentReadId) this.agentReadId = cursor
      }
      // 对方撤回消息（如客服撤回其回复）
      this._onWsRevoke = (payload) => {
        if (!payload || !payload.messageId) return
        if (this.session && payload.sessionId && Number(payload.sessionId) !== Number(this.session.id)) return
        this.applyRevoked(Number(payload.messageId))
      }
      // 自己撤回消息的回执（服务端已处理成功）
      this._onWsRevokeAck = (payload) => {
        if (payload && payload.messageId) {
          this.applyRevoked(Number(payload.messageId))
        }
      }
      this._onWsClose = () => { this.wsReady = false }
      this._onWsError = () => { this.wsReady = false }

      chatSocket.on('open', this._onWsOpen)
      chatSocket.on('message', this._onWsMessage)
      chatSocket.on('ack', this._onWsAck)
      chatSocket.on('read', this._onWsRead)
      chatSocket.on('revoke', this._onWsRevoke)
      chatSocket.on('revokeAck', this._onWsRevokeAck)
      chatSocket.on('close', this._onWsClose)
      chatSocket.on('error', this._onWsError)
      this.wsReady = chatSocket.isOpen
    },
    unbindSocket () {
      if (this._onWsOpen) chatSocket.off('open', this._onWsOpen)
      if (this._onWsMessage) chatSocket.off('message', this._onWsMessage)
      if (this._onWsAck) chatSocket.off('ack', this._onWsAck)
      if (this._onWsRead) chatSocket.off('read', this._onWsRead)
      if (this._onWsRevoke) chatSocket.off('revoke', this._onWsRevoke)
      if (this._onWsRevokeAck) chatSocket.off('revokeAck', this._onWsRevokeAck)
      if (this._onWsClose) chatSocket.off('close', this._onWsClose)
      if (this._onWsError) chatSocket.off('error', this._onWsError)
      this._onWsOpen = null
      this._onWsMessage = null
      this._onWsAck = null
      this._onWsRead = null
      this._onWsRevoke = null
      this._onWsRevokeAck = null
      this._onWsClose = null
      this._onWsError = null
    },

    // 轮询仅作为 WebSocket 不可用时的兜底方案
    startPolling () {
      this.stopPolling()
      this.pollTimer = setInterval(() => {
        if (chatSocket.isOpen) return
        this.loadMessages().catch(() => {})
      }, 3000)
    },
    stopPolling () {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    // 拉取新消息（markRead=1：聊天页内拉取即推进“买家已读游标”，供客服端显示已读回执）
    async loadMessages () {
      if (!this.session) return
      const { data } = await getChatMessagesApi(this.session.id, this.lastId, 1)
      // 同步客服已读游标：客服看过我的消息后，我的消息气泡显示「已读」
      const agentRead = Number(data.session && data.session.agent_last_read_id) || 0
      if (agentRead > this.agentReadId) {
        this.agentReadId = agentRead
      }
      if (data.messages && data.messages.length) {
        this.appendMessages(data.messages)
      }
      // 无论本次是否拉到新消息，都滚动到最底部显示最新消息
      this.$nextTick(() => this.scrollToBottom())
    },
    // 追加消息（自动按 id 去重、同步已读位与内存缓存）
    appendMessages (list) {
      if (!list || !list.length) return
      let added = false
      list.forEach(msg => {
        if (!msg || !msg.id) return
        if (this.messages.some(m => Number(m.id) === Number(msg.id))) return
        // 记录「剩余可撤回秒数」的获取时刻，便于本地倒计时（服务端已按 MySQL 时钟计算）
        msg._revokeLeftAt = Date.now()
        this.messages.push(msg)
        if (Number(msg.id) > this.lastId) this.lastId = Number(msg.id)
        added = true
      })
      if (!added) return
      // 同步「新消息提示」的已读位置，避免离开聊天页后对已读消息重复提示
      setChatNotifiedMsgId(this.lastId)
      // 更新内存缓存，避免再次进入聊天页重复加载历史
      if (this.session) setChatMessageCache(this.session.id, this.messages, this.lastId)
      this.$nextTick(() => this.scrollToBottom())
    },

    // ==================== 消息撤回（2 分钟内可撤回） ====================
    // 该消息剩余可撤回秒数：优先用服务端返回的剩余秒数（按 MySQL 时钟计算），
    // 本地再按「获取后经过的时间」递减；无该字段（历史缓存 / 旧数据）时按消息时间兜底估算。
    revokeLeftSeconds (msg) {
      if (!msg || msg.revoked === 1 || Number(msg.sender_type) !== 1) return 0
      const base = Number(msg.revoke_left_seconds)
      if (!isFinite(base)) {
        // 兜底：用消息时间估算（'2026-09-22 01:13:53' 形式在 Safari 需替换为 '/' 才能解析）
        const created = new Date(String(msg.create_time || '').replace(/-/g, '/')).getTime()
        if (!created || isNaN(created)) return 0
        return Math.max(0, 120 - Math.floor((Date.now() - created) / 1000))
      }
      const fetchedAt = Number(msg._revokeLeftAt) || Date.now()
      return Math.max(0, base - Math.floor((Date.now() - fetchedAt) / 1000))
    },
    // 长按菜单统一用它判断是否可撤回（仅自己发送且 2 分钟内的消息）
    canRevokeMsg (msg) {
      return this.revokeLeftSeconds(msg) > 0
    },
    // 长按 600ms 弹出操作菜单（买家 / 客服的消息都可以长按操作，方便自由复制）
    onMsgTouchStart (msg) {
      this.longPressTriggered = false
      this.cancelMsgLongPress()
      if (!msg || msg.revoked === 1) return
      this.longPressTimer = setTimeout(() => {
        this.longPressTimer = null
        this.longPressTriggered = true
        this.openMsgMenu(msg)
      }, 600)
    },
    // 鼠标长按（电脑浏览器调试 / 桌面端访问时同样可用）
    onMsgMouseDown (msg) {
      if (!msg || msg.revoked === 1) return
      this.longPressTriggered = false
      this.cancelMsgLongPress()
      this.longPressTimer = setTimeout(() => {
        this.longPressTimer = null
        this.longPressTriggered = true
        this.openMsgMenu(msg)
      }, 600)
    },
    onMsgMouseUp (msg, event) {
      this.onMsgTouchEnd(msg, event)
    },
    onMsgTouchEnd (msg, event) {
      // 长按已触发：屏蔽随后的 click（如图片预览）
      if (this.longPressTriggered && event && typeof event.preventDefault === 'function') {
        event.preventDefault()
      }
      this.cancelMsgLongPress()
      // 稍后复位标记，避免影响后续点击（移动端 click 可能在 touchend 后约 300ms 才触发）
      setTimeout(() => { this.longPressTriggered = false }, 500)
    },
    cancelMsgLongPress () {
      if (this.longPressTimer) {
        clearTimeout(this.longPressTimer)
        this.longPressTimer = null
      }
    },
    // 右键（桌面端）：已选中文字时保留系统菜单（可直接点「复制」），否则弹出操作菜单
    onMsgContextMenu (msg, event) {
      if (this.hasTextSelection()) return
      if (event && typeof event.preventDefault === 'function') event.preventDefault()
      this.openMsgMenu(msg)
    },
    // 当前是否存在文字选区（用于桌面端右键保留系统复制菜单）
    hasTextSelection () {
      const sel = window.getSelection && window.getSelection()
      return !!(sel && sel.rangeCount && String(sel.toString() || '').trim())
    },
    // 打开消息操作菜单（复制 / 选择文字 / 复制链接 / 查看大图 / 撤回）
    // 买家与客服发出的消息都支持长按操作，方便像微信一样自由复制
    openMsgMenu (msg) {
      if (!msg || msg.revoked === 1) return
      const actions = []
      if (this.canRevokeMsg(msg)) {
        actions.push({ name: '撤回', key: 'revoke', color: '#ff6b35' })
      }
      if (Number(msg.msg_type) === 2) {
        actions.push({ name: '查看大图', key: 'preview' })
      } else if (msg.content) {
        actions.push({ name: '复制', key: 'copy' })
        actions.push({ name: '选择文字', key: 'select' })
      }
      if (findLinks(msg.content).length) {
        actions.push({ name: Number(msg.msg_type) === 2 ? '复制图片链接' : '复制链接', key: 'copyLink' })
      }
      if (!actions.length) return
      this.activeMsg = msg
      this.msgMenuActions = actions
      this.showMsgMenu = true
    },
    onMsgMenuSelect (action) {
      const msg = this.activeMsg
      this.showMsgMenu = false
      if (!msg || !action) return
      if (action.key === 'revoke') {
        this.doRevoke(msg)
      } else if (action.key === 'copy') {
        this.copyMsgText(msg.content)
      } else if (action.key === 'select') {
        this.openTextSelect(msg)
      } else if (action.key === 'copyLink') {
        this.copyMsgLink(msg)
      } else if (action.key === 'preview') {
        this.previewImage(msg.content)
      }
    },
    // 复制整条消息（Clipboard API 不可用时自动回退 execCommand，http 环境同样可用）
    async copyMsgText (text) {
      const value = String(text === null || text === undefined ? '' : text)
      if (!value) {
        Toast('没有可复制的内容')
        return
      }
      const ok = await copyText(value)
      Toast(ok ? '已复制' : '复制失败，请长按文字手动复制')
    },
    // ==================== 消息文字自由复制（选择任意片段） ====================
    // 打开「选择文字」弹层：弹层内可长按拖动选中任意片段后复制
    openTextSelect (msg) {
      const value = String((msg && msg.content) || '')
      if (!value) return
      this.selectText = value
      this.showTextSelect = true
      this.$nextTick(() => this.selectAllText())
    },
    // 打开弹层时默认全选（移动端可再长按拖动调整选区，桌面端可直接 Ctrl+C）
    selectAllText () {
      const el = this.$refs.selectTextBox
      if (!el || !window.getSelection || !document.createRange) return
      try {
        const range = document.createRange()
        range.selectNodeContents(el)
        const sel = window.getSelection()
        sel.removeAllRanges()
        sel.addRange(range)
      } catch (e) {
        // 部分浏览器限制脚本选中，忽略即可，用户仍可长按手动选择
      }
    },
    // 取弹层中当前选中的文字（选区不在弹层内时视为未选中）
    getSelectedText () {
      const el = this.$refs.selectTextBox
      const sel = window.getSelection && window.getSelection()
      if (!el || !sel || !sel.rangeCount) return ''
      const text = String(sel.toString() || '')
      if (!text.trim()) return ''
      const inside = (node) => !!node && (node === el || (el.contains && el.contains(node)))
      if (!inside(sel.anchorNode) && !inside(sel.focusNode)) return ''
      return text
    },
    // 复制选中的片段（未选择时给出提示，避免误以为已复制）
    async copySelection () {
      const selected = this.getSelectedText()
      if (!selected) {
        Toast('请先长按文字并拖动选择要复制的内容')
        return
      }
      const ok = await copyText(selected)
      Toast(ok ? '已复制选中的内容' : '复制失败，请长按文字手动复制')
    },
    // 复制消息里的网址（一条消息含多个网址时先让用户选择）
    copyMsgLink (msg) {
      const links = findLinks(msg && msg.content)
      if (!links.length) {
        Toast('未识别到网址')
        return
      }
      if (links.length === 1) {
        this.copyMsgText(links[0])
        return
      }
      this.linkActions = links.map(link => ({ name: link, key: link }))
      this.showLinkMenu = true
    },
    onLinkMenuSelect (action) {
      this.showLinkMenu = false
      if (action && action.name) this.copyMsgText(action.name)
    },
    // ==================== 消息中的网址（点击打开） ====================
    // 文本 → 片段（普通文字 / 链接），带缓存避免重复正则解析
    msgSegments (text) {
      const key = String(text === null || text === undefined ? '' : text)
      if (!key) return []
      const cache = this._segCache || (this._segCache = {})
      const cached = cache[key]
      if (cached) return cached
      const segments = splitTextLinks(key)
      if (Object.keys(cache).length > 300) this._segCache = {}
      this._segCache[key] = segments
      return segments
    },
    // 点击消息里的链接：站内链接直接路由跳转，站外链接进内置浏览器（顶部 / 底部都能返回商城）
    onLinkClick (href) {
      const url = String(href || '').trim()
      if (!url) return
      if (isInternalUrl(url)) {
        const path = toInternalPath(url)
        if (path && path !== this.$route.fullPath) this.$router.push(path)
        return
      }
      if (!isHttpUrl(url)) {
        Toast('该链接暂不支持打开')
        return
      }
      this.$router.push({ path: '/webview', query: { url } })
    },
    // 执行撤回：WebSocket 在线时走 WS（实时广播给客服），否则走 HTTP
    async doRevoke (msg) {
      if (!msg || !this.session) return
      if (!this.canRevokeMsg(msg)) {
        Toast('超过 2 分钟的消息不能撤回')
        return
      }
      if (this.revoking) return

      // 二次确认，避免误触（点击「撤回」入口 / 长按菜单都走这里）
      try {
        await Dialog.confirm({
          title: '撤回消息',
          message: '撤回后双方都将看不到这条消息，确定撤回吗？',
          confirmButtonText: '撤回',
          cancelButtonText: '取消'
        })
      } catch (e) {
        return
      }

      this.revoking = true
      try {
        const sid = this.session.id
        const mid = Number(msg.id)
        let ok = false
        if (chatSocket.isOpen) {
          ok = chatSocket.sendRevokeMessage(sid, mid)
        }
        if (!ok) {
          await revokeChatMessageApi(mid)
          ok = true
        }
        if (ok) {
          this.applyRevoked(mid)
          Toast('已撤回')
        }
      } catch (e) {
        Toast(typeof e === 'string' ? e : '撤回失败')
      } finally {
        this.revoking = false
      }
    },
    // 本地标记消息为已撤回（内容隐藏 + 显示撤回提示）
    applyRevoked (messageId) {
      const msg = this.messages.find(m => Number(m.id) === Number(messageId))
      if (!msg) return
      if (msg.revoked === 1) return
      msg.revoked = 1
      msg.content = ''
      msg.revoke_left_seconds = 0
      if (this.session) setChatMessageCache(this.session.id, this.messages, this.lastId)
    },
    // 图片点击预览（长按撤回时不触发）
    onImageClick (msg) {
      if (this.longPressTriggered) return
      this.previewImage(msg.content)
    },
    // 头像加载失败时回退到本地默认头像
    onAvatarError () {
      if (this.agentAvatar !== this.defaultAvatar) this.agentAvatar = this.defaultAvatar
      if (this.userAvatar !== this.defaultAvatar) this.userAvatar = this.defaultAvatar
    },
    // 等待 WebSocket 消息回执（超时返回 null，调用方走 HTTP 兜底）
    waitForAck (clientMsgId, timeout = 3000) {
      return new Promise(resolve => {
        const timer = setTimeout(() => {
          delete this.pendingAcks[clientMsgId]
          resolve(null)
        }, timeout)
        this.pendingAcks[clientMsgId] = (message) => {
          clearTimeout(timer)
          resolve(message || null)
        }
      })
    },
    scrollToBottom () {
      const el = this.$refs.messageList
      if (el) {
        el.scrollTop = el.scrollHeight
      }
    },
    // 发送文本消息
    async send () {
      if (!this.inputText) {
        Toast('请输入内容')
        return
      }
      const content = this.inputText
      const ok = await this.sendMsg(content, 1)
      if (ok) this.inputText = ''
    },
    // 发送消息（msgType: 1=文本 2=图片）
    // 优先走 WebSocket；WebSocket 不可用（或回执超时）时自动降级为 HTTP 接口，保证消息不丢
    async sendMsg (content, msgType) {
      if (!content) return false
      if (this.sending) return false
      this.sending = true
      try {
        const clientMsgId = 'c' + Date.now() + Math.random().toString(16).slice(2)
        const viaWs = chatSocket.isOpen &&
          chatSocket.sendChatMessage(this.session.id, content, msgType, clientMsgId)
        if (viaWs) {
          const ackMsg = await this.waitForAck(clientMsgId)
          if (ackMsg) {
            this.appendMessages([ackMsg])
            return true
          }
          // 已通过 WebSocket 发出但未收到回执：不做 HTTP 重发（避免产生重复消息），
          // 改为重新拉取一次消息列表进行校准
          await this.loadMessages().catch(() => {})
          return true
        }
        // HTTP 兜底（WebSocket 未连接时）
        const { data } = await sendChatMessageApi(this.session.id, content, msgType)
        this.appendMessages([data])
        return true
      } catch (err) {
        Toast(typeof err === 'string' ? err : '发送失败，请稍后重试')
        return false
      } finally {
        this.sending = false
      }
    },
    // 选择图片后上传并发送
    async onFileChange (e) {
      const file = e.target.files && e.target.files[0]
      e.target.value = ''
      if (!file) return
      if (!/^image\//.test(file.type)) {
        Toast('请选择图片文件')
        return
      }
      if (file.size > 5 * 1024 * 1024) {
        Toast('图片大小不能超过 5MB')
        return
      }
      try {
        const { data } = await uploadChatImageApi(file)
        await this.sendMsg(data.url, 2)
      } catch (err) {
        Toast(typeof err === 'string' ? err : '图片上传失败，请重试')
      }
    },
    // 打开图片查看器（支持双击 / 双指捏合 / 滚轮缩放，拖动查看细节）
    previewImage (url) {
      if (!url) return
      // 缩略图作为加载占位（缩略图不存在时留空，直接加载原图）
      this.previewThumb = this.failedThumbs[url] ? '' : this.thumbUrl(url)
      this.previewUrl = url
      this.previewVisible = true
    },
    // 生成缩略图地址（服务器上传时生成 xxx_thumb.jpg）
    thumbUrl (url) {
      if (!url || this.failedThumbs[url]) return url
      // 已上传时生成的缩略图为 jpg：xxx_thumb.jpg
      const idx = url.lastIndexOf('.')
      if (idx <= url.lastIndexOf('/')) return url
      return url.slice(0, idx) + '_thumb.jpg'
    },
    // 缩略图加载失败（旧图片没有缩略图）时回退显示原图
    onThumbError (url) {
      if (url && !this.failedThumbs[url]) {
        this.$set(this.failedThumbs, url, true)
      }
    }
  }
}
</script>

<style lang="less" scoped>
.chat-page {
  height: 100vh;
  height: 100dvh; /* 兼容移动端浏览器地址栏收起/展开 */
  background: #f5f5f5;
  display: flex;
  flex-direction: column;
  padding-top: 46px;
  /deep/ .van-nav-bar__arrow { color: #333; }
  /deep/ .van-nav-bar__text { color: #333; }

  .not-login {
    text-align: center;
    padding: 80px 20px;

    p {
      color: #666;
      margin: 16px 0 24px;
      font-size: 15px;
    }
  }

  .loading {
    text-align: center;
    padding: 80px 20px;

    p {
      color: #999;
      margin-top: 16px;
      font-size: 14px;
    }
  }

  .chat-body {
    flex: 1;
    overflow: hidden;

    .message-list {
      height: 100%;
      overflow-y: auto;
      padding: 16px 12px calc(54px + env(safe-area-inset-bottom));
      box-sizing: border-box;

      .empty-tip {
        text-align: center;
        color: #999;
        font-size: 13px;
        padding: 40px 20px;
        background: #fff;
        border-radius: 8px;
      }

      .message-item {
        display: flex;
        align-items: flex-end;
        margin-bottom: 16px;

        // 撤回的消息：居中显示系统提示
        &.recalled {
          display: block;
          text-align: center;

          .recalled-tip {
            display: inline-block;
            max-width: 80%;
            padding: 4px 12px;
            border-radius: 10px;
            background: rgba(0, 0, 0, 0.06);
            color: #999;
            font-size: 12px;
            line-height: 18px;
          }
        }

        .avatar {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          flex-shrink: 0;
          background: #fff;
          object-fit: cover;
        }

        // 点击客服头像 → 进入店铺主页
        .avatar-clickable {
          cursor: pointer;
        }

        .msg-col {
          display: flex;
          flex-direction: column;
          max-width: 65%;
          min-width: 0;

          .bubble {
            max-width: 100%;
            padding: 10px 12px;
            border-radius: 10px;
            font-size: 15px;
            line-height: 22px;
            word-break: break-all;
            white-space: pre-wrap;
            // 移动端：长按气泡由自定义菜单接管（复制 / 选择文字），避免与系统长按选择冲突
            user-select: none;
            -webkit-user-select: none;
            -webkit-touch-callout: none;
          }

          // 消息中的网址：点击即可打开（站外链接进内置浏览器，顶部 / 底部都能返回商城）
          .msg-link {
            color: #2b6cff;
            text-decoration: underline;
            cursor: pointer;
          }

          // 桌面端（鼠标）：气泡内文字可直接拖动选中，配合 Ctrl+C 自由复制
          @media (hover: hover) and (pointer: fine) {
            .bubble {
              user-select: text;
              -webkit-user-select: text;
            }
          }

          // 图片消息气泡
          .bubble-img {
            padding: 4px;
            background: #fff;
            line-height: 0;
            cursor: pointer;
            border-radius: 10px;

            img {
              display: block;
              max-width: 160px;
              max-height: 160px;
              border-radius: 6px;
              object-fit: cover;
            }
          }

          // 已读回执（我发出的消息：客服是否已读）
          .read-state {
            align-self: flex-end;
            font-size: 11px;
            line-height: 1.3;
            color: #c9c9cc;
            margin-top: 3px;
            padding-right: 4px;

            &.read {
              color: #07c160;
            }
          }
        }

        &.agent {
          justify-content: flex-start;

          .msg-col {
            margin-left: 8px;
            align-items: flex-start;
          }

          .bubble {
            background: #fff;
            color: #333;
            border-top-left-radius: 2px;
          }
        }

        &.mine {
          justify-content: flex-end;

          .avatar {
            order: 2;
            margin-left: 8px;
          }

          .msg-col {
            align-items: flex-end;
          }

          .bubble {
            background: linear-gradient(90deg, #ecb53c, #ff9211);
            color: #fff;
            border-top-right-radius: 2px;

            .msg-link {
              color: #fff;
            }
          }
        }
      }
    }
  }

  .chat-input {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 10;
    display: flex;
    align-items: center;
    padding: 8px 12px;
    padding-bottom: calc(8px + env(safe-area-inset-bottom));
    background: #fff;
    border-top: 1px solid #eee;

    .inp {
      flex: 1;
      height: 36px;
      border: none;
      outline: none;
      background: #f5f5f5;
      border-radius: 18px;
      padding: 0 14px;
      font-size: 14px;
    }

    .img-btn {
      font-size: 24px;
      color: #ff9211;
      margin-right: 10px;
      flex-shrink: 0;
    }

    .send-btn {
      width: 64px;
      height: 36px;
      margin-left: 10px;
      border: none;
      border-radius: 18px;
      background: linear-gradient(90deg, #ecb53c, #ff9211);
      color: #fff;
      font-size: 14px;
    }
  }

  // 选择文字弹层：消息文字可自由选中任意片段后复制
  .text-select-popup {
    padding: 16px 16px calc(20px + env(safe-area-inset-bottom));

    .ts-header {
      display: flex;
      align-items: center;
      justify-content: space-between;

      .ts-title {
        font-size: 16px;
        font-weight: 600;
        color: #333;
      }

      .ts-close {
        font-size: 18px;
        color: #999;
        padding: 4px;
      }
    }

    .ts-tip {
      margin: 10px 0 12px;
      font-size: 12px;
      line-height: 18px;
      color: #999;
    }

    .ts-text {
      max-height: 40vh;
      overflow-y: auto;
      padding: 12px;
      border-radius: 8px;
      background: #f5f5f5;
      font-size: 15px;
      line-height: 22px;
      word-break: break-all;
      white-space: pre-wrap;
      color: #333;
      // 允许长按拖动选中任意片段（并保留系统「复制」菜单）
      user-select: text;
      -webkit-user-select: text;
      -webkit-touch-callout: default;
    }

    .ts-actions {
      margin-top: 16px;

      /deep/ .van-button + .van-button {
        margin-top: 10px;
      }
    }
  }

  // 图片查看器见 components/ImageViewer.vue（全屏遮罩 + 缩放 / 拖动 / 适应宽度）

}
</style>